import React, { useState, useRef, useMemo } from "react";
import cx from "classnames";
import { Map } from "immutable";
import { useDrag, useDrop } from "react-dnd";
import { Icon } from "@showitinc/showit-wc";
import IDUtils from "../../../utils/IDUtils";
import BlockUtils from "../../../utils/BlockUtils";
import EventUtils from "../../../utils/EventUtils";
import AppConstants from "../../../constants/AppConstants";
import UIActions from "../../../actions/UIActions";
import LayerPanelElement from "./LayerPanelElement";
import LayerPanelBlockState from "./LayerPanelBlockState";
import LayerPanelBlockStateDropMarker from "./LayerPanelBlockStateDropMarker";
import LayerPanelBlockMenu from "./LayerPanelBlockMenu";
import uiPrefix from "../../../utils/UiPrefixUtils";
import slugify from "../../../utils/SlugifyUtils";
import openRowContextMenu from "../../../utils/openRowContextMenu";
import { generateDragSpec, generateDropSpec, getTargetArea } from "../../../utils/DnDUtils";
import {
  BLOCK_LAYER_ITEM,
  BLOCK_STATE_LAYER_ITEM,
  ELEMENT_LAYER_ITEM,
} from "../../../constants/DnDConstants";
import EmptyLayer from "./EmptyLayer";
import { pagePanelDrop } from "../SitePanelDropHandlers";
import styles from "./LayerPanelBlock.module.css";
import DataTypes, { GROUP } from "@/constants/DataTypes";
import LayerPanelGroup from "./LayerPanelGroup";
import { useGroupPanelOpen } from "@/hooks/useGroupPanelOpen";
import { DROP_AREA_TOP } from "@/constants/LayerConstants";

const {
  BLOCK_PROP_PARENT_BLOCK,
  DROP_SHADOW_TOP,
  DROP_SHADOW_BOTTOM,
  DROP_SHADOW_CENTER,
  ITEM_TYPE_BLOCK,
  ITEM_TYPE_ELEMENT,
  ITEM_TYPE_STATE,
  VISIBLE_DESKTOP,
  VISIBLE_MOBILE,
  VISIBLE_NONE,
} = AppConstants;

const VisibilityIcon = ({ visible }) =>
  ({
    [VISIBLE_MOBILE]: <Icon type="desktopOff" size="20" className={styles.visible} />,
    [VISIBLE_DESKTOP]: <Icon type="mobileOff" size="20" className={styles.visible} />,
    [VISIBLE_NONE]: (
      <div className={styles["device-container"]}>
        <Icon type="mobileOff" size="20" />
        <Icon type="desktopOff" size="20" />
      </div>
    ),
  }[visible] || null);

const getBlogState = (block) =>
  (block.getIn(["wp", "stackViews"], false) === 1 && block.getIn(["wp", "type"]) === "post") ||
  block.getIn(["wp", "type"]) === "posts_lookup";

const LayerPanelBlock = ({
  block = Map(),
  id,
  index,
  layerType,
  onLayerMove,
  onSelect,
  onVisibleChange,
}) => {
  const [dropArea, setDropArea] = useState(null);
  const [selectedChildren, setSelectedChildren] = useState({});
  const [selectedCanvasView, setSelectedCanvasView] = useState(null);
  const [isGroupOpen, setIsGroupOpen] = useGroupPanelOpen(id);
  const selfRef = useRef();

  const _nonCanvasViewElementSelected = useMemo(
    () =>
      Object.entries(selectedChildren).some(
        ([key, value]) => !key.includes("states") && Boolean(value)
      ),
    [selectedChildren]
  );

  const drag = generateDragSpec({
    type: BLOCK_LAYER_ITEM,
    item: {
      id,
      index,
      layerType,
    },
    onDrop: ({ data }) => onLayerMove(data),
  });
  const drop = generateDropSpec({
    type: [BLOCK_LAYER_ITEM, BLOCK_STATE_LAYER_ITEM, ELEMENT_LAYER_ITEM],
    onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current)),
    onDrop: pagePanelDrop({ dropRef: selfRef.current, id, index, layerType }),
  });

  const [{ dragItemType, isDragging }, dragRef] = useDrag(drag);
  const [{ canDrop, isOverCurrent }, dropRef] = useDrop(drop);

  const _selectElement = (elemId, elemType, e) => {
    const layout = block.getIn(["selected", "layout"]);
    const { multi, range } = EventUtils.selectionType(e) || {};

    if (!multi && elemType === GROUP) {
      const groupData = block.getIn(IDUtils.getArray(elemId, 1));
      const groupId = groupData?.get("id");
      groupData && setIsGroupOpen((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    }

    UIActions.selectElement({
      id: elemId,
      type: elemType,
      layout,
      blockId: block.get("id"),
      addTo: multi,
      addRange: range,
      from: "panel",
    });
  };

  const _selectState = (stateId) => {
    const layout = block.getIn(["selected", "layout"]);
    const state = block.getIn(["selected", "blockState"]);
    const statePath = BlockUtils.trimIndexFromPath(stateId);
    const blockId = block.get("id");

    if (state === stateId) {
      // eslint-disable-next-line no-param-reassign
      stateId = null;
    }
    UIActions.selectBlockState({
      state: stateId,
      statePath,
      layout,
      blockId,
      from: "panel",
    });
  };

  // eslint-disable-next-line no-shadow
  const _getStates = (block, statesIndex, selected) => {
    const blockId = block.get("id");
    const states = block.get("states");
    const elements = block.get("elements");
    const selectedState = selected != null ? selected.get("blockState") : null;
    const isBlogState = getBlogState(block);
    const hasStatesIndex = statesIndex > -1;
    const statesKey = hasStatesIndex ? BlockUtils.getElementID(blockId, statesIndex) : null;

    return (
      <div
        data-e2e={uiPrefix(`canvas-views-wrapper-${slugify(label)}`)}
        className={styles["canvas-views-wrapper"]}
        key={IDUtils.getID(blockId, "states")}
      >
        {hasStatesIndex && (
          <LayerPanelBlockStateDropMarker
            key="states-marker-top"
            id={statesKey}
            index={statesIndex + 1}
            onLayerMove={onLayerMove}
            height={statesIndex === elements.size - 1 ? 7 : 3}
          />
        )}
        {states &&
          states.map((state, idx) => {
            const stateKey = BlockUtils.getStateID(blockId, idx);
            const selectedItem = stateKey === selectedState;

            return (
              <LayerPanelBlockState
                id={stateKey}
                key={stateKey}
                block={block}
                layerType={ITEM_TYPE_STATE}
                index={idx}
                state={state}
                isBlogState={isBlogState}
                selected={selectedItem}
                onSelect={() => _selectState(stateKey)}
                onLayerMove={onLayerMove}
                onVisibleChange={onVisibleChange}
                selectedChildren={selectedChildren}
                setSelectedChildren={setSelectedChildren}
                selectedCanvasView={selectedCanvasView}
                setSelectedCanvasView={setSelectedCanvasView}
                nonCanvasViewElementSelected={_nonCanvasViewElementSelected}
              />
            );
          })}

        {hasStatesIndex && (
          <LayerPanelBlockStateDropMarker
            key="states-marker-bottom"
            id={statesKey}
            index={statesIndex}
            height={statesIndex === 0 ? 7 : 3}
            onLayerMove={onLayerMove}
          />
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-shadow
  const _getStatesDisplayOnly = (block, siteBlock, selected) => {
    const blockId = block.get("id");
    const states = siteBlock.get("states");
    const selectedState = selected ? selected.get("blockState") : null;
    const isBlogState = getBlogState(block);

    return (
      <div
        data-e2e={uiPrefix(`canvas-views-wrapper-display-only-${slugify(label)}`)}
        key={IDUtils.getID(blockId, "states")}
      >
        {states &&
          states.map((state, idx) => {
            const stateKey = BlockUtils.getSiteBlockStateID(blockId, idx);
            const selectedItem = stateKey === selectedState;
            return (
              <LayerPanelBlockState
                id={stateKey}
                key={stateKey}
                layerType={ITEM_TYPE_STATE}
                index={idx}
                isBlogState={isBlogState}
                state={state}
                selected={selectedItem}
                onSelect={() => _selectState(stateKey)}
                displayOnly
                selectedChildren={selectedChildren}
                setSelectedChildren={setSelectedChildren}
                selectedCanvasView={selectedCanvasView}
                setSelectedCanvasView={setSelectedCanvasView}
                nonCanvasViewElementSelected={_nonCanvasViewElementSelected}
              />
            );
          })}
      </div>
    );
  };

  const _rename = (e) => {
    e.stopPropagation();
    e.preventDefault();
    UIActions.toggleRenamePopup({
      title: "Canvas",
      itemType: ITEM_TYPE_BLOCK,
      itemId: block.get("id"),
      name: block.get("name"),
      slug: block.get("slug"),
      showWarning: true,
    });
  };

  const active = !!block.get("selected");
  const label = block.get("name");
  const visible = block.get("visible");
  const isSiteCanvas = BlockUtils.isUsingSiteBlock(block) || BlockUtils.isSiteBlock(block);

  const blockHeadingStyle = {};
  const renderers = [];

  if (active) {
    const hasSiteBlock = BlockUtils.hasSiteBlock(block);
    const selected = block.get("selected");

    if (hasSiteBlock) {
      const siteBlock = block.get(BLOCK_PROP_PARENT_BLOCK);

      renderers.unshift(_getStatesDisplayOnly(block, siteBlock, selected));
    } else {
      const elements = block.get("elements");

      if (elements?.size > 0) {
        elements.forEach((element, idx) => {
          const elemType = element?.get("type");
          const elemKey = BlockUtils.getElementID(block.get("id"), idx);
          const elemId = element.get("id");
          const lastElement = !idx;

          if (elemType !== "states") {
            const layer = (
              <LayerPanelElement
                elemKey={elemKey}
                id={elemKey}
                key={elemKey}
                index={idx}
                element={element}
                layerType={ITEM_TYPE_ELEMENT}
                onSelect={(e) => _selectElement(elemKey, elemType, e)}
                onVisibleChange={onVisibleChange}
                onLayerMove={onLayerMove}
                selected={selected}
                isGroupOpen={!!isGroupOpen[elemId]}
                selectedChildren={selectedChildren}
                setSelectedChildren={setSelectedChildren}
                selectedCanvasView={selectedCanvasView}
                setSelectedCanvasView={setSelectedCanvasView}
                nonCanvasViewElementSelected={_nonCanvasViewElementSelected}
              />
            );
            if (element.get("type") === GROUP) {
              renderers.unshift(
                <LayerPanelGroup
                  elemKey={elemKey}
                  element={element}
                  groupLayer={layer}
                  group={element}
                  id={elemKey}
                  isGroupOpen={!!isGroupOpen[elemId]}
                  index={idx}
                  key={`${elemId}-wrapper`}
                  layerType={ITEM_TYPE_ELEMENT}
                  onSelect={(e) => _selectElement(elemKey, elemType, e)}
                  onVisibleChange={onVisibleChange}
                  onLayerMove={onLayerMove}
                  selected={selected}
                  selectedChildren={selectedChildren}
                  setSelectedChildren={setSelectedChildren}
                  selectedCanvasView={selectedCanvasView}
                  setSelectedCanvasView={setSelectedCanvasView}
                  nonCanvasViewElementSelected={_nonCanvasViewElementSelected}
                  showBottomDropArea={lastElement}
                />
              );
            } else {
              renderers.unshift(layer);
            }
          } else if (elemType === "states") {
            renderers.unshift(_getStates(block, idx, selected));
          }
        });
      } else {
        renderers.push(<EmptyLayer key="empty" />);
      }
    }
  }

  if (canDrop && isOverCurrent && !isDragging) {
    if (dragItemType === BLOCK_LAYER_ITEM) {
      blockHeadingStyle.boxShadow =
        dropArea === DROP_AREA_TOP ? DROP_SHADOW_TOP : DROP_SHADOW_BOTTOM;
    } else {
      blockHeadingStyle.boxShadow = DROP_SHADOW_CENTER;
    }
  }

  const siteCanvasClass = isSiteCanvas ? "layer-block layer-site-canvas" : "layer-block";

  const _themeClass = cx(styles.root, {
    [styles.active]: active,
    [siteCanvasClass]: siteCanvasClass,
  });

  const _handleSelect = (e) => {
    if (!active) {
      setSelectedChildren({});
      setSelectedCanvasView(null);
    }
    onSelect(e);
  };

  return (
    <div
      id={id}
      ref={(self) => {
        selfRef.current = self;
        dropRef(self);
      }}
      className={`layer-block-wrapper ${styles["layer-panel-block"]}`}
      data-testid="layer-block-wrapper"
      onContextMenu={(e) =>
        openRowContextMenu({ event: e, rowEl: selfRef.current, active, select: _handleSelect })
      }
    >
      <div
        className={`layer-block ${_themeClass}`}
        data-testid="layer-block"
        data-e2e={uiPrefix(`canvas-layer-panel-${slugify(label)}`)}
        onClick={_handleSelect}
        {...(!isSiteCanvas && { "data-intercom-target": "page canvas" })}
        ref={dragRef}
        style={blockHeadingStyle}
      >
        <div className={styles["left-container"]}>
          <Icon type={isSiteCanvas ? "siteCanvas" : "pageCanvas"} size="20" />
          <div onDoubleClick={_rename}>{label}</div>
        </div>
        <div className={styles["right-container"]}>
          <div>
            <VisibilityIcon visible={visible} />
          </div>
          <div
            className={cx(styles.menu, { [styles["menu-active"]]: !active })}
            onClick={(e) => {
              if (active) {
                e.stopPropagation();
              }
            }}
          >
            <LayerPanelBlockMenu block={block} blockIndex={index} active={active} />
          </div>
        </div>
      </div>
      {renderers}
    </div>
  );
};

export default LayerPanelBlock;
